import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import { IgTypography } from "@intelligems/ig-theme";
import { GraphConfig } from "@intelligems/ig-types";
import { Skeleton, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { EstImpact } from "@src/features/analytics/baseDashboard/chartComponents/EstImpact";
import { UpliftPlusMinus } from "@src/features/analytics/baseDashboard/UpliftPlusMinus";
import { getUplift } from "@src/features/analytics/baseDashboard/utils";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { PoweredByIntelligems } from "@src/images/Icons/Intelligems/PoweredByIntelligems";
import {
  getExperienceAnalyticsFilters,
  getExperienceAnalyticsPageName,
} from "@src/store/slices/dashboardFilters";
import { getShareSettings } from "@src/store/slices/global";
import { selectedVariations } from "@src/store/slices/variations";
import {
  BarMetricData,
  GraphChangeLabelStatus,
  GraphMetricData,
} from "@src/types/analytics";

import { getGraphChangeLabelColor, getGraphChangeLabelStatus } from "../utils";

export const UpliftImpactSkeleton = () => {
  return (
    <Grid container direction={"column"}>
      <Grid>
        <Skeleton
          variant="rectangular"
          width={150}
          height={39}
          sx={{ borderRadius: "5px" }}
        />
      </Grid>
      <Grid sx={{ marginTop: "15px" }}>
        <Skeleton
          variant="rectangular"
          width={150}
          height={23}
          sx={{ borderRadius: "5px" }}
        />
      </Grid>
    </Grid>
  );
};

interface UpliftImpactProps {
  config: GraphConfig;
  data: GraphMetricData;
  isSharing: boolean;
  testDuration: number;
}

export const UpliftImpact = ({
  data,
  config,
  isSharing,
  testDuration,
}: UpliftImpactProps) => {
  const variations = useSelector(selectedVariations);
  const pageName = useSelector(getExperienceAnalyticsPageName);
  const shareSettings = useSelector(getShareSettings());
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
  const graphValueFormatter = useGraphValueFormatter();
  const theme = useTheme();
  const controlVariationId = variations.find(
    (variation) => variation.isControl
  )!.id;
  const upliftWrapper = useMemo(() => {
    return getUplift(
      data as BarMetricData[],
      dashboardFilters.compareGroupB!,
      graphValueFormatter,
      controlVariationId === dashboardFilters.compareGroupA!
    );
  }, [data, dashboardFilters.compareGroupB]);

  const getStatus = (): GraphChangeLabelStatus => {
    if (
      config.impactCalculationColoring === "neutral" ||
      upliftWrapper?.change === "none"
    )
      return "neutral";
    return getGraphChangeLabelStatus(
      parseFloat(upliftWrapper?.uplift),
      config.impactCalculationColoring === "inverted"
    );
  };

  const status = getStatus();
  const color = getGraphChangeLabelColor(status, theme);

  return (
    <Grid container justifyContent={"space-between"}>
      <Grid>
        <Grid container alignItems={"center"} sx={{ textAlign: "start" }}>
          {upliftWrapper.uplift ? (
            <Grid container alignItems={"center"} justifyContent={"flex-start"}>
              <Grid>
                {status === "positive" ? (
                  <ArrowUpIcon
                    size={16}
                    stroke={color}
                    strokeWidth={3}
                    style={{ marginRight: "4px" }}
                  />
                ) : status === "negative" ? (
                  <ArrowDownIcon
                    size={16}
                    stroke={color}
                    strokeWidth={3}
                    style={{ marginRight: "4px" }}
                  />
                ) : null}
              </Grid>
              <Grid>
                <IgTypography
                  variant={"h1"}
                  sx={{
                    fontWeight: 600,
                    color: color,
                  }}
                >
                  {!isSharing || (isSharing && shareSettings.showChange)
                    ? upliftWrapper?.uplift
                    : "XXX.XX%"}
                </IgTypography>
              </Grid>
              {upliftWrapper?.plusMinus ? (
                <Grid ml={1}>
                  <UpliftPlusMinus upliftWrapper={upliftWrapper} />
                </Grid>
              ) : null}
            </Grid>
          ) : (
            <Grid>
              {pageName !== "overview" && (
                <IgTypography
                  variant={"h1"}
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  No Change
                </IgTypography>
              )}
            </Grid>
          )}
        </Grid>

        <EstImpact
          groupMetricData={data}
          compareGroupB={dashboardFilters.compareGroupB!}
          compareGroupA={dashboardFilters.compareGroupA!}
          config={config}
          isSharing={isSharing}
          testDuration={testDuration}
        />
      </Grid>
      {isSharing ? (
        <Grid>
          <Grid sx={{ marginTop: "14px" }}>
            <PoweredByIntelligems color={"accent"} />
          </Grid>
        </Grid>
      ) : null}
    </Grid>
  );
};

interface UpliftImpactWrapperProps {
  config: GraphConfig;
  data?: GraphMetricData;
  isLoading: boolean;
  isSharing: boolean;
  testDuration: number;
}

export const UpliftImpactWrapper = ({
  data,
  isLoading,
  config,
  isSharing,
  testDuration,
}: UpliftImpactWrapperProps) => {
  if (isLoading) {
    return <UpliftImpactSkeleton />;
  } else {
    return (
      <UpliftImpact
        isSharing={isSharing}
        config={config}
        data={data!}
        testDuration={testDuration}
      />
    );
  }
};
